import { api } from './api';
import { User } from '../types';

export interface CreateUserData {
  email: string;
  name: string;
  password: string;
  role?: 'ADMIN' | 'MANAGER' | 'CASHIER';
  locationId?: string;
  employeeCode?: string;
  commissionRate?: number;
  baseSalary?: number;
  hireDate?: string;
}

export interface UpdateUserData {
  email?: string;
  name?: string;
  password?: string;
  role?: 'ADMIN' | 'MANAGER' | 'CASHIER';
  locationId?: string;
  isActive?: boolean;
  employeeCode?: string;
  commissionRate?: number;
  baseSalary?: number;
  hireDate?: string;
}

export interface UsersResponse {
  success: boolean;
  users: User[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export const usersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<UsersResponse, {
      page?: number;
      limit?: number;
      search?: string;
      role?: string;
      isActive?: boolean;
      locationId?: string;
    }>({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        
        if (params.page) searchParams.append('page', params.page.toString());
        if (params.limit) searchParams.append('limit', params.limit.toString());
        if (params.search) searchParams.append('search', params.search);
        if (params.role) searchParams.append('role', params.role);
        if (params.isActive !== undefined) searchParams.append('active', params.isActive.toString());
        if (params.locationId) searchParams.append('locationId', params.locationId);

        // Add cache-busting parameter
        searchParams.append('_t', Date.now().toString());

        return {
          url: `/users?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['User'],
      // Force cache invalidation for fresh data
      keepUnusedDataFor: 0,
    }),
    
    getUser: builder.query<{ success: boolean; data: User }, string>({
      query: (id) => ({
        url: `/users/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'User', id }],
    }),
    
    getUserByEmployeeCode: builder.query<{ success: boolean; data: User }, string>({
      query: (code) => ({
        url: `/users/employee/${code}`,
        method: 'GET',
      }),
      providesTags: ['User'],
    }),
    
    createUser: builder.mutation<{ success: boolean; data: User }, CreateUserData>({
      query: (userData) => ({
        url: '/users',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: ['User'],
    }),
    
    updateUser: builder.mutation<{ success: boolean; data: User }, { id: string; data: UpdateUserData }>({
      query: ({ id, data }) => ({
        url: `/users/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'User', id }],
    }),
    
    deleteUser: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'User', id }],
    }),
    
    changePassword: builder.mutation<{ success: boolean }, { currentPassword: string; newPassword: string }>({
      query: (passwordData) => ({
        url: '/users/change-password',
        method: 'POST',
        body: passwordData,
      }),
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserQuery,
  useGetUserByEmployeeCodeQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useChangePasswordMutation,
} = usersApi;